// UploadExcel.jsx
import React, { useContext, useState } from 'react';
import { Button, Typography, Box, useTheme, Drawer, useMediaQuery, IconButton } from '@mui/material';
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from 'xlsx';
import { tokens } from "../../theme";
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext';
import SettingsIcon from '@mui/icons-material/Settings';
import { Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

const UploadMembers = () => {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const { currentuser, base_url } = useContext(AuthContext);
  const customers = currentuser?.user.organization.customers || [];
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const colors = tokens(theme.palette.mode);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleView = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleEdit = (row) => {
    console.log("Edit:", row);
    // Implement your edit logic here
  };

  const handleDelete = (row) => {
    console.log("Delete:", row);
    // Implement your delete logic here
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const excelData = XLSX.utils.sheet_to_json(firstSheet);

      try {
        const response = await axios.post(base_url + '/ethioglobal/upload-users-data', {
          data: excelData,
          OrganizationId: currentuser?.user.organization.id,
        });
        console.log(response.data);
        alert('Data uploaded successfully!');
      } catch (error) {
        console.error('Error uploading data:', error);
        alert('Error uploading data');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const columns = [
    {
      field: "firstname",
      headerName: "First Name",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "lastname",
      headerName: "Last Name",
      flex: 1,
      hide: isSmallScreen,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      hide: isSmallScreen,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "phonenumber",
      headerName: "Phone Number",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Tooltip title={params.value} arrow>
          <span>{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 1,
      hide: isSmallScreen,
      renderCell: (params) => {
        const date = new Date(params.value);
        const formattedDate = date.toLocaleString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
    
        return (
          <Tooltip title={formattedDate} arrow>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      field: "id",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        const { id } = params.row;

        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Tooltip title="Edit" arrow>
              <IconButton onClick={() => handleEdit(params.row)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete" arrow>
              <IconButton onClick={() => handleDelete(params.row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="View" arrow>
              <IconButton onClick={() => handleView(params.row)}>
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  return (
    <Box m="20px">
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Upload Members (Excel File)
        </Typography>
        <input type="file" accept=".xlsx, .xls" onChange={handleFileChange} />
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          sx={{ mt: 2 }}
        >
          Upload
        </Button>
      </Box>
      <Box
        m="0px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.blueAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
        }}
      >
        {customers.length > 0 ? 
          <>
            <DataGrid 
              rows={customers} 
              columns={columns} 
              pageSize={5} 
              rowsPerPageOptions={[5]} 
              sx={{
                '& .overflow-cell': overflowCellStyle,
              }}
            />
            <Drawer anchor="right" open={open} onClose={handleClose}>
              <div style={{ width: 350, padding: 20, backgroundColor: colors.primary[300], height: '100%', color: theme.palette.text.primary }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h2>Details</h2>
                  <IconButton onClick={handleClose}>
                    <CloseIcon />
                  </IconButton>
                </div>
                {selectedRow ? (
                  <div>
                    <p><strong>First Name:</strong> {selectedRow.firstname}</p>
                    <p><strong>Last Name:</strong> {selectedRow.lastname}</p>
                    <p><strong>Email:</strong> {selectedRow.email}</p>
                    <p><strong>Phone Number:</strong> {selectedRow.phonenumber}</p>
                    <p><strong>Status:</strong> {selectedRow.status}</p>
                    <p><strong>Created At:</strong> {new Date(selectedRow.createdAt).toLocaleString()}</p>
                  </div>
                ) : (
                  <p>No details available.</p>
                )}
              </div>
            </Drawer>
          </>
          :
          <Typography>No rows to show.</Typography>
        }
      </Box>
    </Box>
  );
};

const overflowCellStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default UploadMembers;